import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ToastContext from '../contexts/ToastContext';
import { calculateLevel, getLevelProgress } from '../utils/xpCalculator';
import { getReputationTier } from '../utils/reputationSystem';
import { MdStars, MdTrophy, MdSchool, MdSportsSoccer } from 'react-icons/md';
import AppIcon from './icons/AppIcon';
import '../styles/ProfileStats.css';

const SPORTS_LIST = [
  { id: 'football', name: 'Football', icon: 'match' },
  { id: 'running', name: 'Running', icon: 'activity' },
  { id: 'swimming', name: 'Swimming', icon: 'activity' },
  { id: 'tennis', name: 'Tennis', icon: 'activity' },
  { id: 'basketball', name: 'Basketball', icon: 'activity' },
  { id: 'cycling', name: 'Cycling', icon: 'activity' },
  { id: 'volleyball', name: 'Volleyball', icon: 'activity' },
  { id: 'badminton', name: 'Badminton', icon: 'activity' }
];

export default function ProfileStats() {
  const { currentUser } = useAuth();
  const toast = useContext(ToastContext);
  const [selectedSport, setSelectedSport] = useState('football');
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      // Simulate fetching user stats from API
      setTimeout(() => {
        setUserStats(currentUser);
        setLoading(false);
      }, 500);
    }
  }, [currentUser]);

  if (loading) {
    return <div className="profile-stats-loading">Loading profile...</div>;
  }

  if (!userStats) {
    return <div className="profile-stats-empty">No user data available</div>;
  }

  const level = calculateLevel(userStats.totalXP || 0);
  const levelProgress = getLevelProgress(userStats.totalXP || 0);
  const reputationTier = getReputationTier(userStats.reputationScore || 100);
  const sportStats = userStats.sportStats?.[selectedSport] || {};
  const sportLevel = calculateLevel(sportStats.totalXP || 0);

  return (
    <div className="profile-stats-container">
      {/* Global Profile Section */}
      <div className="profile-global-section">
        <div className="profile-avatar-block">
          {userStats.photoURL ? (
            <img src={userStats.photoURL} alt={userStats.displayName} className="profile-avatar" />
          ) : (
            <div className="profile-avatar-placeholder">{userStats.firstName?.charAt(0)}</div>
          )}
          <div className="level-badge">
            <div className="level-number">{level}</div>
            <div className="level-text">LEVEL</div>
          </div>
        </div>

        <div className="profile-info">
          <h2 className="profile-name">{userStats.displayName || `${userStats.firstName} ${userStats.lastName}`}</h2>
          <p className="profile-email">{userStats.email}</p>

          {/* Reputation Score */}
          <div className="reputation-card">
            <div className="reputation-header">
              <span className="reputation-icon"><AppIcon name={reputationTier.icon} size={20} /></span>
              <div>
                <div className="reputation-title">{reputationTier.label}</div>
                <div className="reputation-description">{reputationTier.description}</div>
              </div>
            </div>
            <div className="reputation-bar">
              <div
                className="reputation-bar-fill"
                style={{
                  width: `${userStats.reputationScore || 100}%`,
                  backgroundColor: reputationTier.color
                }}
              />
            </div>
            <div className="reputation-value">{userStats.reputationScore || 100}/100</div>
          </div>
        </div>
      </div>

      {/* Global XP & Level Progress */}
      <div className="xp-progress-section">
        <h3>GLOBAL PROGRESS</h3>
        <div className="xp-stats">
          <div className="xp-stat">
            <span className="xp-label">Total XP</span>
            <span className="xp-value">{userStats.totalXP || 0}</span>
          </div>
          <div className="xp-stat">
            <span className="xp-label">Matches Completed</span>
            <span className="xp-value">{userStats.completedMatches || 0}</span>
          </div>
          <div className="xp-stat">
            <span className="xp-label">Fair Play Rating</span>
            <span className="xp-value">⭐ {userStats.fairPlayRating || 5}/5</span>
          </div>
        </div>

        <div className="level-progress-card">
          <div className="progress-header">
            <span>Level {level}</span>
            <span className="next-level">Level {level + 1}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${levelProgress.progress}%` }} />
          </div>
          <div className="progress-info">
            <span className="current-xp">{levelProgress.currentLevelXP - userStats.totalXP || 0} XP to next level</span>
            <span className="progress-percentage">{levelProgress.progress}%</span>
          </div>
        </div>
      </div>

      {/* Badges Section */}
      {userStats.badges && userStats.badges.length > 0 && (
        <div className="badges-section">
          <h3>ACHIEVEMENTS ({userStats.badges.length})</h3>
          <div className="badges-grid">
            {userStats.badges.map((badge, idx) => (
              <div key={idx} className="badge-item">
                <div className="badge-icon">🏅</div>
                <div className="badge-name">{badge.name}</div>
                <div className="badge-date">
                  {new Date(badge.unlockedAt || Date.now()).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sport-Specific Stats */}
      <div className="sports-section">
        <h3>SPORT STATS</h3>

        {/* Sport Tabs */}
        <div className="sport-tabs">
          {SPORTS_LIST.map(sport => (
            <button
              key={sport.id}
              className={`sport-tab ${selectedSport === sport.id ? 'active' : ''}`}
              onClick={() => setSelectedSport(sport.id)}
              title={sport.name}
            >
              <span className="sport-icon"><AppIcon name={sport.icon} size={18} /></span>
              <span className="sport-name">{sport.name}</span>
            </button>
          ))}
        </div>

        {/* Sport Stats Card */}
        <div className="sport-stats-card">
          <div className="sport-header">
            <h4>
              <AppIcon name={SPORTS_LIST.find(s => s.id === selectedSport)?.icon} size={20} />{' '}
              {SPORTS_LIST.find(s => s.id === selectedSport)?.name}
            </h4>
            <div className="sport-level">Level {sportLevel}</div>
          </div>

          {Object.keys(sportStats).length > 0 ? (
            <div className="sport-stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><AppIcon name="target" size={22} strokeWidth={1.8} /></div>
                <div className="stat-name">Matches</div>
                <div className="stat-value">{sportStats.matches || 0}</div>
              </div>

              {sportStats.wins !== undefined && (
                <div className="stat-card">
                  <div className="stat-icon"><AppIcon name="trophy" size={22} strokeWidth={1.8} /></div>
                  <div className="stat-name">Wins</div>
                  <div className="stat-value">{sportStats.wins || 0}</div>
                </div>
              )}

              {sportStats.goals !== undefined && (
                <div className="stat-card">
                  <div className="stat-icon"><AppIcon name="match" size={22} strokeWidth={1.8} /></div>
                  <div className="stat-name">Goals</div>
                  <div className="stat-value">{sportStats.goals || 0}</div>
                </div>
              )}

              {sportStats.assists !== undefined && (
                <div className="stat-card">
                  <div className="stat-icon"><AppIcon name="activity" size={22} strokeWidth={1.8} /></div>
                  <div className="stat-name">Assists</div>
                  <div className="stat-value">{sportStats.assists || 0}</div>
                </div>
              )}

              {sportStats.totalDistance !== undefined && (
                <div className="stat-card">
                  <div className="stat-icon"><AppIcon name="activity" size={22} strokeWidth={1.8} /></div>
                  <div className="stat-name">Distance (km)</div>
                  <div className="stat-value">{(sportStats.totalDistance || 0).toFixed(1)}</div>
                </div>
              )}

              {sportStats.points !== undefined && (
                <div className="stat-card">
                  <div className="stat-icon"><AppIcon name="activity" size={22} strokeWidth={1.8} /></div>
                  <div className="stat-name">Points</div>
                  <div className="stat-value">{sportStats.points || 0}</div>
                </div>
              )}

              {sportStats.mvpCount !== undefined && (
                <div className="stat-card">
                  <div className="stat-icon"><AppIcon name="star" size={22} strokeWidth={1.8} /></div>
                  <div className="stat-name">MVP Awards</div>
                  <div className="stat-value">{sportStats.mvpCount || 0}</div>
                </div>
              )}

              <div className="stat-card">
                <div className="stat-icon"><AppIcon name="zap" size={22} strokeWidth={1.8} /></div>
                <div className="stat-name">XP</div>
                <div className="stat-value">{sportStats.totalXP || 0}</div>
              </div>
            </div>
          ) : (
            <div className="no-data">No stats yet in {SPORTS_LIST.find(s => s.id === selectedSport)?.name}</div>
          )}
        </div>
      </div>

      {/* Match History */}
      {userStats.matchHistory && userStats.matchHistory.length > 0 && (
        <div className="match-history-section">
          <h3>RECENT MATCHES</h3>
          <div className="match-history-list">
            {userStats.matchHistory.slice(0, 5).map((match, idx) => (
              <div key={idx} className={`match-history-item ${match.result}`}>
                <div className="match-result-badge" title={match.result}>
                  {match.result === 'win' ? '✓' : match.result === 'loss' ? '✗' : '='}
                </div>
                <div className="match-info">
                  <div className="match-sport">{match.sport}</div>
                  <div className="match-date">{new Date(match.date).toLocaleDateString()}</div>
                </div>
                <div className="match-xp">+{match.xpEarned || 0} XP</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
